'use client';
import React, { useEffect, useRef } from 'react';
import AppImage from '@/components/ui/AppImage';

export default function HeroSection() {
  const scrollRef = useRef<HTMLDivElement>(null);

  const scrollToReveal = () => {
    const el = document.getElementById('reveal-stages');
    if (el) el?.scrollIntoView({ behavior: 'smooth' });
  };

  return (
    <section className="relative w-full overflow-hidden" style={{ height: '100vh', minHeight: '600px' }}>
      {/* Video / Image background */}
      <div className="absolute inset-0 z-0">
        <AppImage
          src="/assets/images/ChatGPT_Image_Jul_12__2026_at_10_18_16_PM-1783966036180.jpeg"
          alt="Black luxury sedan in corporate parking garage under dramatic lighting, ready for professional detailing"
          fill
          className="object-cover object-center"
          priority />
        
        {/* Cinematic color grade overlay */}
        <div
          className="absolute inset-0"
          style={{
            background: 'linear-gradient(135deg, rgba(13,13,13,0.7) 0%, rgba(13,13,13,0.4) 50%, rgba(13,13,13,0.75) 100%)'
          }} />
        
        <div className="hero-overlay absolute inset-0" />
      </div>
      {/* Content */}
      <div className="relative z-10 flex flex-col justify-end h-full max-w-7xl mx-auto px-6 pb-20 md:pb-28">
        <div className="max-w-3xl">
          <div className="metric-pill mb-6 hero-headline" style={{ animationDelay: '0.2s' }}>
            <span className="w-1.5 h-1.5 rounded-full bg-ceramic inline-block" style={{ background: 'var(--ceramic-blue)' }} />
            Mobile Detailing · Stanislaus County & Surrounding Areas
          </div>

          <h1
            className="hero-headline font-manrope font-extrabold text-white leading-none tracking-tight mb-6"
            style={{ fontSize: 'clamp(42px, 7vw, 90px)', lineHeight: '0.96', letterSpacing: '-0.03em' }}>
            
            For those who<br />
            <span style={{ color: 'var(--polished-aluminum)' }}>expect</span>{' '}
            <span
              style={{
                background: 'linear-gradient(135deg, #ffffff 0%, #D4D4D8 50%, #3B82F6 100%)',
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent',
                backgroundClip: 'text'
              }}>
              more.
            </span>
          </h1>

          <p
            className="hero-sub font-dm text-lg md:text-xl mb-10 max-w-xl leading-relaxed"
            style={{ color: 'rgba(212,212,216,0.75)', fontWeight: 300 }}>
            
            Mobile detailing services — delivered to your office, garage, or driveway. Book in 60 seconds.
          </p>

          <div className="hero-ctas flex flex-wrap gap-4 mb-10">
            <a href="#book" className="btn-primary">
              Book Your Detail
              <svg width="16" height="16" fill="none" stroke="currentColor" strokeWidth="2.5" viewBox="0 0 24 24">
                <path d="M5 12h14M13 5l7 7-7 7" strokeLinecap="round" strokeLinejoin="round" />
              </svg>
            </a>
          </div>

          {/* Risk of Not Using a Detailer */}
          <div className="mb-10 max-w-xl">
            <p className="font-manrope font-semibold text-sm uppercase tracking-widest mb-3" style={{ color: 'var(--ceramic-blue)' }}>
              The Risk of Not Using a Detailer
            </p>
            <ul className="space-y-2">
              {[
                'Paint oxidation & swirl marks that permanently dull your finish',
                'Contaminants that etch into clear coat over time',
                'Interior grime buildup that degrades materials and resale value',
              ]?.map((risk) => (
                <li key={risk} className="flex items-start gap-2 text-sm" style={{ color: 'rgba(212,212,216,0.75)', fontFamily: 'DM Sans, sans-serif' }}>
                  <span className="mt-1 flex-shrink-0 w-1.5 h-1.5 rounded-full" style={{ background: '#ef4444', marginTop: '6px' }} />
                  {risk}
                </li>
              ))}
            </ul>
          </div>

          {/* Hero stats */}
          <div className="hero-stats flex flex-wrap gap-8 md:gap-12">
            {[
            { value: '340+', label: 'Vehicles Corrected' },
            { value: '3+', label: 'Years in Business' }]?.
            map((stat) =>
            <div key={stat?.label}>
                <div
                className="font-manrope font-extrabold text-white"
                style={{ fontSize: '28px', letterSpacing: '-0.03em' }}>
                
                  {stat?.value}
                </div>
                <div style={{ fontSize: '12px', color: 'var(--aluminum-dim)', fontFamily: 'DM Sans, sans-serif', letterSpacing: '0.06em', textTransform: 'uppercase', marginTop: '2px' }}>
                  {stat?.label}
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
      {/* Scroll indicator */}
      <button
        onClick={scrollToReveal}
        className="absolute bottom-8 right-8 z-10 flex flex-col items-center gap-2 opacity-60 hover:opacity-100 transition-opacity"
        aria-label="Scroll to see detailing stages">
        
        <span style={{ fontSize: '10px', fontFamily: 'Manrope, sans-serif', fontWeight: 700, letterSpacing: '0.1em', textTransform: 'uppercase', color: 'var(--aluminum-dim)' }}>
          Scroll
        </span>
        <div
          className="w-px h-12"
          style={{ background: 'linear-gradient(to bottom, var(--aluminum-dim), transparent)' }} />
        
      </button>
    </section>
  );

}